import { TopMenuActions, topMenuBuider } from "../components/menu/top/Builder";
import { showDoubleActionPopUp } from "@zing/shared/components/popup/doubleAction";
import { showToast } from "@zing/shared/components/toast";
import { useRoleSwitch } from "@zing/active-tenant/contexts/RoleSwitchProvider";
import { updateProfileImageLink } from "@zing/shared/domain/feature/hub/user-config/profileImage";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router";
import { useSmartNavigate } from "@zing/shared/smartNav/useSmartNavigate";
import { useSmartNavInterceptor } from "@zing/shared/smartNav/useSmartNavInterceptor";
import {
  api,
  deleteProvider,
  getProvider,
  updateProvider,
} from "@zing/shared/api";
import { DeleteFormat } from "../api/hooks/mutation/contracts";
import { logout, setAuth, setPermissions } from "@zing/shared/auth";
import { breadCrumbBuilder } from "@zing/shared/breadCrumb/Builder";
import { ROUTES } from "@zing/shared/constants";
import { cookies, setCookie, removeCookie } from "@zing/shared/cookies/behavior";
import { getStartRoute, getHubRoute } from "@zing/shared/handlers";
import { regexPatterns } from "@zing/shared/regex";
import { isSponsorEmployee } from "@zing/active-tenant/auth/isSponsorEmployee";
import { loggedUser } from "@zing/shared/user/LoggedUser";
import { changeSidebarState, store } from "@zing/shared/store";
import { TReducers } from "../store/reducers";
import { decodeJwtPayload } from "@zing/shared/utils/decodeJwtPayload";

const MOBILE_BREAKPOINT_PX = 576;

// TODO: ⚠️ Known security debt (ADR-056 · E7 · decision-009): authorization decided
// on the client, without consulting the backend. Infers whether the user is a sponsor
// (CAIXA) employee from the token's role or the email domain — neither is a reliable
// identity check. Only goes away once the backend starts returning these permissions
// ready-made; until then, isolated here instead of scattered across the hook.
const inferSponsorEmployee = (
  tokenPayload: ReturnType<typeof decodeJwtPayload>,
  email?: string
): boolean =>
  tokenPayload
    ? tokenPayload?.role === "CaixaEmployee"
    : email?.includes("@caixa") ?? false;

const buildFabricatedSponsorPermissions = () => ({
  id: "00000000-0000-0000-0000-000000000000",
  name: "CaixaEmployee",
  features: [
    {
      id: "00000000-0000-0000-0000-000000000001",
      code: "caixa_employee",
      access: [
        { id: "00000000-0000-0000-0000-000000000001", code: "view" },
        { id: "00000000-0000-0000-0000-000000000002", code: "edit" },
        { id: "00000000-0000-0000-0000-000000000003", code: "launch" },
      ],
    },
    {
      id: "00000000-0000-0000-0000-000000000002",
      code: "idea",
      access: [
        { id: "00000000-0000-0000-0000-000000000004", code: "view" },
        { id: "00000000-0000-0000-0000-000000000005", code: "edit" },
        { id: "00000000-0000-0000-0000-000000000006", code: "launch" },
      ],
    },
    {
      id: "00000000-0000-0000-0000-000000000003",
      code: "evaluation",
      access: [
        { id: "00000000-0000-0000-0000-000000000007", code: "view" },
        { id: "00000000-0000-0000-0000-000000000008", code: "edit" },
        { id: "00000000-0000-0000-0000-000000000009", code: "launch" },
      ],
    },
  ],
});

// The impersonation state has to survive the app-* -> legacy-* hop. It was React state in
// each app, so the legacy always booted with isBehavingAs=false: it hid the "back to company
// selection" button and the menu, and showed the company search instead. Persist it in a
// cookie on the shared parent domain — the same mechanism the session itself already uses.
const BEHAVE_AS_COOKIE = "_behaveAsCompany";

export const useInnerPageHooks = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDarkModeOn, setDarkMode] = useState(false);

  useLayoutEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches
    ) {
      setIsSidebarOpen(false);
    }
  }, []);

  const [filterText, setFilterText] = useState<string>("");
  const [companies, setCompanies] = useState<any[] | null>([]);
  const { mutate: getMutate } = getProvider();
  const { mutate: updateMutate } = updateProvider();
  const [isBehavingAs, setBehavingAs] = useState<boolean>(
    () => cookies.get(BEHAVE_AS_COOKIE) !== undefined
  );
  const [forceRenderKey, setForceRenderKey] = useState<number>(0);
  const [user] = useState(loggedUser.get());
  const [isSuperAdmin] = useState<boolean>(user.isSuperAdmin);
  const userAuth = useSelector((state: TReducers) => state.auth);

  const filteredCompanies = useMemo(() => {
    if (!filterText.trim()) {
      return companies;
    }

    return companies?.filter((company) =>
      company.contactName
        .toLowerCase()
        .includes(filterText.toLowerCase().trim())
    );
  }, [companies, filterText]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  const isDarkMode = useSelector(
    (state: TReducers) => state.isDarkModeOn.isDarkModeOn
  );
  const featureType = useSelector((state: TReducers) =>
    state.auth.permissions?.features?.map((feature) => feature.code)
  );
  const { selectedRole, isCaixaUser, setSelectedRole } = useRoleSwitch();

  const onSuccessDeletion = async (): Promise<void> => {
    setCookie("_banner_update_late", false);
    setCookie("_userUpdatedAt", null);
    // dispatch(logout()) clears the auth cookies (_cred etc.) synchronously inside the
    // reducer — navigate() must run after it, not in parallel, so hasAuthToken() already
    // reads false by the time useSmartNavigate() decides shell vs. legacy for /login.
    dispatch(logout());
    navigate(ROUTES.USER_AUTH.LOGIN);
  };

  const navigate = useSmartNavigate();
  useSmartNavInterceptor();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const { mutate: deleteAuth } = deleteProvider(onSuccessDeletion);
  const { mutate: deleteMutate } = deleteProvider();

  const handleDeleteAuth = (): void => {
    const request: DeleteFormat = {
      url: "/v1/auth/login",
    };
    deleteAuth(request);
  };
  const handleLogout = (): void => {
    showDoubleActionPopUp({
      title: "Logout",
      message: "Deseja realmente se desconectar?",
      buttons: [{ text: "Sim" }, { text: "Não" }],
      action: async () => handleDeleteAuth(),
    });
  };
  const topMenuActions: TopMenuActions = {
    logoutAction: handleLogout,
  };
  // const user = loggedUser.get();
  const selectedProfileName =
    (cookies.get("_selectedProfileName") as string) || "";
  const menuTopItems = topMenuBuider.build(topMenuActions, user);

  const breadcrumb = breadCrumbBuilder.build();

  useEffect(() => {
    dispatch(changeSidebarState(isSidebarOpen));
  }, [isSidebarOpen, dispatch]);

  const handleCompanySelect = useCallback(
    (menuItem) => {
      if (menuItem.id && !menuItem.path) {
        updateMutate(
          {
            url: `/v1/manager/access/behavior/behave-as/company/${menuItem.id}`,
          },
          {
            onSuccess: () => {
              queueMicrotask(() => {
                setCookie(BEHAVE_AS_COOKIE, menuItem.id);
                setBehavingAs(true);
                setForceRenderKey((prev) => prev + 1); // Force re-render
                setBehaveAs(user);

                // `/hub/dashboard`, not a bare "dashboard". useSmartNavigate passes the string through
                // as-is, and an unprefixed path matches no shell route, so smartNav falls through to the
                // legacy host and lands on /dashboard — which is not a route on either app (404).
                navigate(getHubRoute(ROUTES.HUB.DASHBOARD));
              });
            },
            onError: (error) => {
              queueMicrotask(() => {
                removeCookie(BEHAVE_AS_COOKIE);
                setBehavingAs(false);
                setForceRenderKey((prev) => prev + 1);
                console.error("Error switching to company:", error);
              });
            },
          }
        );
      }
    },
    [updateMutate, user]
  );

  const handleBackToCompanySelection = useCallback(() => {
    deleteMutate(
      {
        url: "/v1/manager/access/behavior/behave-as/company",
      },
      {
        onSuccess: (success) => {
          if (success.status === 200) {
            queueMicrotask(() => {
              removeCookie(BEHAVE_AS_COOKIE);
              setBehavingAs(false);
              setFilterText("");
              setForceRenderKey((prev) => prev + 1);

              // `/hub/dashboard`, not a bare "dashboard". useSmartNavigate passes the string through
              // as-is, and an unprefixed path matches no shell route, so smartNav falls through to the
              // legacy host and lands on /dashboard — which is not a route on either app (404).
              navigate(getHubRoute(ROUTES.HUB.DASHBOARD));
            });
          }
        },
        onError: (error) => {
          console.error("Error switching back to company selection:", error);
        },
      }
    );
  }, [deleteMutate]);

  const handleGetCompanyData = useCallback(async () => {
    try {
      getMutate(
        { url: "/v1/manager/company" },
        {
          onSuccess: (response) => {
            if (response?.data.rows) {
              const companies = response.data.rows.map((company: any) => {
                return {
                  title: company?.contactName || company?.name,
                  id: company?.id,
                  name: company?.contactName || company?.name,
                  path: undefined,
                };
              });
              setCompanies(companies);
            }
          },
          onError: (error) => {
            console.error("Erro ao obter dados da empresa:", error);
          },
        }
      );
    } catch (error) {
      console.error("Erro ao obter dados da empresa:", error);
    }
  }, [getMutate]);

  useEffect(() => {
    if (user.isSuperAdmin) {
      handleGetCompanyData();
    }
  }, [user.isSuperAdmin]);

  const isFormPage = regexPatterns.isFormPage.test(location.pathname);

  const setBehaveAs = async (data) => {
    try {
      if (!data) {
        console.error("Dados de companhia inválidos:", data);
        showToast("error", "Erro interno: dados de companhia inválidos");
        return;
      }

      const tokenPayload = decodeJwtPayload(data.data?.token);
      const isSponsorEmployee = inferSponsorEmployee(tokenPayload, data.email);

      if (data.id) {
        if (isSponsorEmployee) {
          setUserProfileImage(data.id, isSponsorEmployee).then(() => {
            setUserPermissions(isSponsorEmployee);
          });
        } else {
          setUserProfileImage(data.id, isSponsorEmployee);
          setUserPermissions(isSponsorEmployee);
        }
      } else {
        setUserPermissions(isSponsorEmployee);
      }
    } catch (error) {
      console.error("Erro ao configurar login:", error);
      showToast("error", "Erro interno ao configurar login");
    }
  };

  const setUserPermissions = async (
    isSponsorEmployee: boolean = false
  ): Promise<void> => {
    try {
      if (isSponsorEmployee) {
        dispatch(setPermissions(buildFabricatedSponsorPermissions()));
        const permissions = ["caixa_employee", "idea", "evaluation"];

        const startRoute = getStartRoute(permissions);

        return;
      }

      const response = await api.get("/v1/system/user/my-permissions");

      if (response.status === 200) {
        let permissionsData = response?.data?.data;

        dispatch(setPermissions(permissionsData));
        const permissions: string[] = [];
        permissionsData.features?.map((feature, index): any => {
          permissions.push(String(feature.code));
          return feature;
        });

        const startRoute = getStartRoute(permissions);
      } else {
        throw new Error(`Status inválido: ${response.status}`);
      }
    } catch (error) {
      if (isSponsorEmployee) {
        dispatch(setPermissions(buildFabricatedSponsorPermissions()));
        navigate("/hub/startup/challenge", { replace: true });
      } else {
        showToast(
          "error",
          "Erro ao carregar permissões. Tente fazer login novamente."
        );
        console.error(
          "Erro fatal: não foi possível carregar permissões para usuário não-Caixa"
        );
      }
    }
  };

  const setUserProfileImage = async (
    userId: string,
    isSponsorEmployee: boolean = false
  ): Promise<void> => {
    try {
      const noImage =
        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460__340.png";

      const endpoint = isSponsorEmployee
        ? `/v1/system/caixa-employee`
        : `/v1/system/user/${userId}`;

      const response = await api.get(endpoint);
      if (response.status === 200) {
        let profileImageLink = noImage;

        if (isSponsorEmployee) {
          profileImageLink =
            response.data.data?.profileImage ||
            response.data.data?.avatar ||
            response.data.data?.profileImageUrl ||
            noImage;

          const userData = response.data.data;
          if (userData) {
            const completeUserData = {
              id: userId,
              data: {
                ...userData,
                token: store.getState().auth.data?.token, // Manter o token atual
                temporaryToken: store.getState().auth.data?.temporaryToken, // Manter o temporaryToken
                name:
                  userData.name ||
                  userData.userName ||
                  userData.fullName ||
                  "Usuário",
                email: userData.email || userData.userEmail || "",
                role: {
                  id: userData.roleId || "00000000-0000-0000-0000-000000000000",
                  name: userData.role || "CaixaEmployee",
                },
                company: {
                  id:
                    userData.companyId ||
                    "00000000-0000-0000-0000-000000000000",
                  name: userData.companyName || "Caixa Econômica Federal",
                },
              },
              isAuthenticated: true,
            };

            store.dispatch(setAuth(completeUserData));
          }
        } else {
          profileImageLink = response.data.data?.profileImage || noImage;
        }

        store.dispatch(updateProfileImageLink(profileImageLink));
      } else {
        store.dispatch(updateProfileImageLink(noImage));
      }
    } catch (error) {
      const noImage =
        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460__340.png";
      store.dispatch(updateProfileImageLink(noImage));
    }
  };

  // On hard reload Redux is empty even though the user is authenticated via cookie.
  // Re-fetch permissions so the menu builds correctly without requiring a new login.
  useEffect(() => {
    if (userAuth.isAuthenticated && (!featureType || featureType.length === 0)) {
      setUserPermissions(isSponsorEmployee());
    }
  }, []);

  return {
    isSidebarOpen,
    setIsSidebarOpen,
    isDarkModeOn,
    setDarkMode,
    filteredCompanies,
    filterText,
    handleFilterChange,
    isBehavingAs,
    isSuperAdmin,
    user,
    menuTopItems,
    breadcrumb,
    isFormPage,
    handleCompanySelect,
    handleBackToCompanySelection,
    selectedProfileName,
    isCaixaUser,
    setSelectedRole,
    selectedRole,
    forceRenderKey, // Export this to use as a key prop
  };
};
